import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getToken } from "@/lib/consult/authSession";
import { api as consultApi } from "@/lib/consult/api";
import { api as ppApi } from "@/lib/pp/api";
import PharmacyFormDialog from "@/components/pp/PharmacyFormDialog";
import { formatPhone } from "@/lib/formatPhone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Copy, Plus, Search, Trash2, X, Settings2, Users, Pencil, Eye, EyeOff } from "lucide-react";

// Survey intake (Outreach → Survey tab). Clients who had HCV call the survey
// line for a paid $20 survey. Registration starts at the approved pharmacy
// where they saw the poster; one paid survey per person every 12 months with
// full survey history per client; "Copy client info" carries the identity
// block into a new consultation. Payments follow the Incentives-HQ pattern
// (pending → approved → paid).

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const sapi = axios.create({ baseURL: `${BACKEND}/api/survey` });
sapi.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers["X-Auth-Token"] = t;
  return config;
});

const NAVY = "#1F3A5F";
const GREEN = "#2E7D5B";
const BORDER = "#E3E0D9";
const MUTED = "#6B7280";
const TEXT = "#1F2937";
const RED = "#B4351B";
const AMBER = "#B77900";

const card = { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: 16 };
const input = { width: "100%", height: 38, padding: "0 10px", fontSize: 13, border: `1px solid ${BORDER}`, borderRadius: 8, boxSizing: "border-box", fontFamily: "inherit" };
// Rule: every field label aligns with the text inside the input below it
// (inputs use 10px horizontal padding, so labels sit 10px in too).
const label = { display: "block", fontSize: 11, fontWeight: 700, color: MUTED, marginBottom: 4, paddingLeft: 10, textTransform: "uppercase", letterSpacing: 0.4 };
// Labels above Yes/No button rows start flush with the buttons (position 0),
// same as the client info and section titles — everything on the form shares
// one left edge; only labels above text/date/select fields stay padded to
// match the text inside those fields.
const labelFlush = { ...label, paddingLeft: 0 };
const btn = (bg, fg = "#fff") => ({ background: bg, color: fg, border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" });
const btnGhost = { background: "#fff", color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
const sech = { fontSize: 11, fontWeight: 800, color: "#4E8878", textTransform: "uppercase", letterSpacing: 1.2, margin: "16px 0 8px" };

const FALLBACK_OUTCOMES = [
  { key: "qualified", label: "Qualified", col: GREEN },
  { key: "lab_work", label: "Lab work", col: AMBER },
  { key: "not_qualified", label: "Not qualified", col: RED },
  { key: "prev_tx", label: "Previously Tx", col: NAVY },
  { key: "self_cured", label: "Self Cured", col: "#2A9F92" },
];
const fmtD = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");

const EMPTY_CLIENT = { first_name: "", last_name: "", dob: "", health_card: "", version_code: "" };
const EMPTY_TX = { medication: "", initial_dispense: "", total_dispensed: "", post_tx_bloodwork: null, post_tx_result: null };
const EMPTY_SURVEY = {
  prev_ab: null, prev_rna: null, prev_rna_result: "", prev_rna_date: "", prev_tx: null,
  treatments: [], risk_addiction_tx: null, risk_pipes: null, risk_needles: null,
  liver_inflammation: null, outcome: "", email: "", phone: "", staff: "",
};

// Same normalization as the consultation form's Health Card Number field:
// digits first (max 10), then a space, then up to 2 version letters.
const formatHCN = (raw) => {
  const cleaned = (raw || "").toUpperCase().replace(/[^0-9A-Z]/g, "");
  const digits = cleaned.replace(/[A-Z]/g, "").slice(0, 10);
  const letters = cleaned.slice(digits.length).replace(/[0-9]/g, "").slice(0, 2);
  if (digits.length < 10) return digits;
  return letters ? `${digits} ${letters}` : digits;
};
// Split a combined "1234567890 AB" entry across the two fields.
const splitHCN = (raw) => {
  const f = formatHCN(raw);
  const [digits, letters = ""] = f.split(" ");
  return { digits, letters };
};

// Age in whole years from an ISO dob (YYYY-MM-DD). "" when no valid date.
const ageFromDob = (dob) => {
  if (!dob) return "";
  const d = new Date(`${String(dob).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? `${age}` : "";
};

const copyClientInfo = (c) => {
  const hc = [c.health_card, c.version_code].filter(Boolean).join(" ");
  const txt = `${c.first_name} ${c.last_name} · ${c.dob}${hc ? ` · ${hc}` : ""}`;
  navigator.clipboard?.writeText(txt)
    .then(() => toast.success("Client info copied — paste it into a new consultation"))
    .catch(() => toast.error("Copy failed"));
};

function YesNo({ value, onChange, testId, yesLabel = "Yes", noLabel = "No" }) {
  const pill = (on, col) => ({
    flex: 1, height: 34, borderRadius: 8, border: `1.5px solid ${on ? col : BORDER}`,
    background: on ? (col === GREEN ? "#E2F2E8" : "#FBEAE5") : "#fff",
    color: on ? col : MUTED, fontWeight: on ? 800 : 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
  });
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <button type="button" data-testid={`${testId}-yes`} style={pill(value === true, GREEN)} onClick={() => onChange(true)}>{yesLabel}</button>
      <button type="button" data-testid={`${testId}-no`} style={pill(value === false, RED)} onClick={() => onChange(false)}>{noLabel}</button>
    </div>
  );
}

export default function SurveyPanel() {
  const [pharmacies, setPharmacies] = useState([]);
  const [medications, setMedications] = useState([]);
  const [view, setView] = useState("dashboard"); // dashboard | newClient
  const [pharmacy, setPharmacy] = useState(null); // pharmacy chosen in the add form
  const [dash, setDash] = useState(null);
  const [dashFilter, setDashFilter] = useState(""); // pharmacy filter on dashboard
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [profile, setProfile] = useState(null); // open profile dialog when set
  const [surveyOpen, setSurveyOpen] = useState(false); // survey form dialog
  const [editingSurveyId, setEditingSurveyId] = useState(null); // survey id when editing
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT);
  const [surveyForm, setSurveyForm] = useState(EMPTY_SURVEY);
  const [payments, setPayments] = useState([]);
  const [payStatusFilter, setPayStatusFilter] = useState(""); // blank until a status is chosen
  const [tileFilter, setTileFilter] = useState(""); // dashboard outcome drill-down
  const [showAddPharmacy, setShowAddPharmacy] = useState(false);
  const [showManagePharmacies, setShowManagePharmacies] = useState(false);
  const [editingSite, setEditingSite] = useState(null);   // full site record when editing
  const [sitesCity, setSitesCity] = useState("");         // Sites popup filters (separate from the dashboard)
  const [sitesType, setSitesType] = useState("");
  const [pharmacyCityFilter, setPharmacyCityFilter] = useState("");
  const [dashType, setDashType] = useState(""); // site type filter (Pharmacy | Shelter | …)
  const [dashMonth, setDashMonth] = useState(""); // "YYYY-MM" or "" = all months
  const [dashStaff, setDashStaff] = useState(""); // "" = all staff
  const [staffList, setStaffList] = useState([]); // [{name, hidden, in_use}]
  const [outcomes, setOutcomes] = useState(FALLBACK_OUTCOMES); // [{key, label, col, in_use}]
  const [showOutcomeMgr, setShowOutcomeMgr] = useState(false);
  const [newOutcome, setNewOutcome] = useState("");
  const [renameOutcomeKey, setRenameOutcomeKey] = useState("");
  const [renameOutcomeValue, setRenameOutcomeValue] = useState("");
  const [showStaffMgr, setShowStaffMgr] = useState(false);
  const [newStaff, setNewStaff] = useState("");
  const [renameName, setRenameName] = useState("");
  const [renameValue, setRenameValue] = useState("");

  const visibleStaff = useMemo(() => staffList.filter((s) => !s.hidden).map((s) => s.name), [staffList]);
  // Dropdown options = visible staff; keep the survey's current value
  // selectable even when that person has since been hidden.
  const staffOptions = useMemo(() => {
    const opts = [...visibleStaff];
    if (surveyForm.staff && !opts.includes(surveyForm.staff)) opts.push(surveyForm.staff);
    return opts;
  }, [visibleStaff, surveyForm.staff]);

  const loadStaff = useCallback(async () => {
    try {
      const { data } = await sapi.get("/staff");
      setStaffList(data.items || []);
    } catch { /* keep current list */ }
  }, []);

  const loadOutcomes = useCallback(async () => {
    try {
      const { data } = await sapi.get("/outcomes");
      if (data.items?.length) setOutcomes(data.items);
    } catch { /* keep current list */ }
  }, []);
  // Outcome label/colour lookup with fallback for legacy keys.
  const outcomeMeta = useCallback((v) =>
    outcomes.find((o) => o.key === v) || FALLBACK_OUTCOMES.find((o) => o.key === v) || { key: v, label: v || "—", col: MUTED },
  [outcomes]);

  const addOutcome = async () => {
    const v = newOutcome.trim();
    if (!v) return;
    try {
      await sapi.post("/outcomes", { label: v });
      setNewOutcome("");
      await loadOutcomes();
      loadDash();
      toast.success(`"${v}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    }
  };
  const saveOutcomeRename = async () => {
    const v = renameOutcomeValue.trim();
    if (!v) return;
    try {
      await sapi.patch(`/outcomes/${renameOutcomeKey}`, { label: v });
      setRenameOutcomeKey("");
      await loadOutcomes();
      loadDash();
      toast.success(`Renamed to "${v}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename");
    }
  };
  const deleteOutcome = async (o) => {
    try {
      await sapi.delete(`/outcomes/${o.key}`);
      await loadOutcomes();
      loadDash();
      toast.success(`"${o.label}" removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const addStaff = async () => {
    const n = newStaff.trim();
    if (!n) return;
    try {
      await sapi.post("/staff", { name: n });
      setNewStaff("");
      await loadStaff();
      toast.success(`"${n}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    }
  };
  const toggleHideStaff = async (s) => {
    try {
      await sapi.patch(`/staff/${encodeURIComponent(s.name)}`, { hidden: !s.hidden });
      await loadStaff();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };
  const saveStaffRename = async () => {
    const n = renameValue.trim();
    if (!n) return;
    try {
      await sapi.patch(`/staff/${encodeURIComponent(renameName)}`, { name: n });
      if (dashStaff === renameName) setDashStaff(n);
      setRenameName("");
      await loadStaff();
      loadDash();
      if (pharmacy) loadClient(pharmacy.id);
      toast.success(`Renamed to "${n}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename");
    }
  };
  const deleteStaff = async (s) => {
    try {
      await sapi.delete(`/staff/${encodeURIComponent(s.name)}`);
      await loadStaff();
      toast.success(`"${s.name}" removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };
  const [newClientCityFilter, setNewClientCityFilter] = useState("");
  const [newClientTypeFilter, setNewClientTypeFilter] = useState("");
  const [siteTypes, setSiteTypes] = useState([]); // pharmacy/shelter/drop-in… from pp settings
  const [editingClient, setEditingClient] = useState(null); // client id when editing
  const [manageMedsOpen, setManageMedsOpen] = useState(false); // shared med list manager
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadPharmacies = useCallback(async () => {
    const { data } = await sapi.get("/pharmacies");
    setPharmacies(data.items || []);
  }, []);
  // Medication rows (id + name) from the shared consultation medication list —
  // same source the consultation Manage medications dialog edits.
  const loadMeds = useCallback(async () => {
    try {
      const { data } = await consultApi.get("/medications");
      const items = data.items || data || [];
      setMedications(items.map((m) => (typeof m === "string" ? { id: m, name: m } : { id: m.id || m._id, name: m.name })));
    } catch {
      setMedications(["Epclusa", "Maviret", "Harvoni", "Vosevi"].map((n) => ({ id: n, name: n })));
    }
  }, []);
  const loadDash = useCallback(async () => {
    const params = {};
    if (dashFilter) params.pharmacy_id = dashFilter;
    else {
      if (pharmacyCityFilter) params.city = pharmacyCityFilter;
      if (dashType) params.site_type = dashType;
    }
    if (dashMonth) params.month = dashMonth;
    if (dashStaff) params.staff = dashStaff;
    const { data } = await sapi.get("/dashboard", { params });
    setDash(data);
  }, [dashFilter, pharmacyCityFilter, dashType, dashMonth, dashStaff]);
  const loadClients = useCallback(async (q = "", pharmacyId = "", outcome = "", month = "", staff = "", city = "", siteType = "") => {
    const params = {};
    if (q) params.q = q;
    if (pharmacyId) params.pharmacy_id = pharmacyId;
    else {
      if (city) params.city = city;
      if (siteType) params.site_type = siteType;
    }
    if (outcome) params.outcome = outcome;
    if (month) params.month = month;
    if (staff) params.staff = staff;
    const { data } = await sapi.get("/clients", { params });
    setClients(data.items || []);
  }, []);
  const loadPayments = useCallback(async (status) => {
    if (!status) { setPayments([]); return; } // blank filter → nothing shown until chosen
    const params = { status };
    if (dashFilter) params.pharmacy_id = dashFilter;
    else {
      if (pharmacyCityFilter) params.city = pharmacyCityFilter;
      if (dashType) params.site_type = dashType;
    }
    if (dashMonth) params.month = dashMonth;
    if (dashStaff) params.staff = dashStaff;
    const { data } = await sapi.get("/payments", { params });
    setPayments(data.items || []);
  }, [dashFilter, pharmacyCityFilter, dashType, dashMonth, dashStaff]);

  useEffect(() => { loadPharmacies(); loadMeds(); loadStaff(); loadOutcomes(); }, [loadPharmacies, loadMeds, loadStaff, loadOutcomes]);
  // The global refresh button (top of every consult page) resets the whole
  // Survey tab: filters, search, open lists — back to the blank default view.
  useEffect(() => {
    const reset = () => {
      setSearch("");
      setPharmacyCityFilter("");
      setDashType("");
      setDashFilter("");
      setDashMonth("");
      setDashStaff("");
      setTileFilter("");
      setPayStatusFilter("");
      setClients([]);
      setPayments([]);
      loadPharmacies();
      loadStaff();
    };
    window.addEventListener("survey:reset", reset);
    return () => window.removeEventListener("survey:reset", reset);
  }, [loadPharmacies, loadStaff]);
  // Location types shared with the referral-site form (Pharmacy, Shelter, …).
  useEffect(() => {
    ppApi.get("/settings/site-types")
      .then(({ data }) => setSiteTypes(data.values || []))
      .catch(() => setSiteTypes([]));
  }, []);
  useEffect(() => { if (view === "dashboard") { loadDash(); loadPayments(payStatusFilter); } }, [view, loadDash, loadPayments, payStatusFilter]);

  const openProfile = async (cid) => {
    const { data } = await sapi.get(`/clients/${cid}`);
    setProfile(data);
  };

  // Dashboard "+ Add" opens the add-patient dialog directly (pharmacy is
  // picked inside the form — no separate pharmacy step).
  const startIntake = () => {
    setEditingClient(null);
    setClientForm(EMPTY_CLIENT);
    setPharmacy(null);
    setNewClientCityFilter("");
    setNewClientTypeFilter("");
    setView("newClient");
  };
  const openEditClient = () => {
    if (!profile) return;
    setEditingClient(profile.id);
    setClientForm({
      first_name: profile.first_name || "",
      last_name: profile.last_name || "",
      dob: profile.dob || "",
      health_card: profile.health_card || "",
      version_code: profile.version_code || "",
    });
    const ph = pharmacies.find((x) => x.id === profile.pharmacy_id) || null;
    setPharmacy(ph);
    setNewClientCityFilter(ph?.city || "");
    setProfile(null);
    setView("newClient");
  };

  // Patient search on the dashboard — global across all pharmacies, and
  // doubles as the duplicate guard (matches already surveyed show a chip).
  useEffect(() => {
    if (view !== "dashboard") return;
    if (!search.trim()) { setClients([]); return; }
    const t = setTimeout(() => { loadClients(search.trim()); }, 250);
    return () => clearTimeout(t);
  }, [search, view, loadClients]);

  // Keep the open tile list in sync when the city/type/location/staff filter changes.
  useEffect(() => {
    if (view !== "dashboard" || !tileFilter || tileFilter === "payments") return;
    loadClients("", dashFilter, tileFilter, dashMonth, dashStaff, pharmacyCityFilter, dashType);
  }, [dashFilter, pharmacyCityFilter, dashType, dashMonth, dashStaff, tileFilter, view, loadClients]);

  const saveClient = async () => {
    if (!pharmacy) return toast.error("Pick the location where they saw the poster");
    if (!clientForm.first_name.trim() || !clientForm.last_name.trim()) return toast.error("First and last name are required");
    if (!clientForm.dob) return toast.error("Date of birth is required");
    setSaving(true);
    try {
      if (editingClient) {
        await sapi.patch(`/clients/${editingClient}`, { ...clientForm, pharmacy_id: pharmacy.id });
        toast.success("Patient updated");
        const cid = editingClient;
        setEditingClient(null);
        setView("dashboard");
        await openProfile(cid);
      } else {
        const { data } = await sapi.post("/clients", { ...clientForm, pharmacy_id: pharmacy.id });
        toast.success(`${data.first_name} added`);
        setSurveyForm(EMPTY_SURVEY);
        setProfile({ ...data, surveys: [], pharmacy_names: { [pharmacy.id]: `${pharmacy.name} — ${pharmacy.city}` } });
        setView("dashboard");
        setSearch("");
        setSurveyOpen(true);
        setNewClientCityFilter("");
        setNewClientTypeFilter("");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const startSurvey = () => {
    setEditingSurveyId(null);
    setSurveyForm(EMPTY_SURVEY);
    setSurveyTried(false);
    setSurveyOpen(true);
  };
  const openEditSurvey = (s) => {
    setEditingSurveyId(s.id);
    setSurveyTried(false);
    setSurveyForm({
      prev_ab: s.prev_ab ?? null,
      prev_rna: s.prev_rna ?? null,
      prev_rna_result: s.prev_rna_result || "",
      prev_rna_date: s.prev_rna_date || "",
      prev_tx: s.prev_tx ?? null,
      treatments: (s.treatments || []).map((t) => ({ ...EMPTY_TX, ...t })),
      risk_addiction_tx: s.risk_addiction_tx ?? null,
      risk_pipes: s.risk_pipes ?? null,
      risk_needles: s.risk_needles ?? null,
      liver_inflammation: s.liver_inflammation ?? null,
      outcome: s.outcome || "",
      email: s.email || "",
      phone: s.phone || "",
      staff: s.staff || "",
    });
    setSurveyOpen(true);
  };

  // Red-frame highlighting for unanswered mandatory fields: after the first
  // save attempt, every incomplete section is outlined until it's filled in.
  const [surveyTried, setSurveyTried] = useState(false);
  const missingSurveyFields = (f) => {
    const miss = new Set();
    for (const k of ["prev_ab", "prev_rna", "prev_tx", "risk_addiction_tx", "risk_pipes", "risk_needles", "liver_inflammation"]) {
      if (f[k] === null || f[k] === undefined) miss.add(k);
    }
    if (f.prev_rna === true) {
      if (!f.prev_rna_result) miss.add("prev_rna_result");
      if (!f.prev_rna_date) miss.add("prev_rna_date");
    }
    if (f.prev_tx === true) {
      if (f.treatments.length === 0) miss.add("treatments");
      f.treatments.forEach((t, i) => {
        if (!t.medication) miss.add(`tx_${i}_medication`);
        if (!t.initial_dispense) miss.add(`tx_${i}_initial_dispense`);
        if (!String(t.total_dispensed).trim()) miss.add(`tx_${i}_total_dispensed`);
        if (t.post_tx_bloodwork === null || t.post_tx_bloodwork === undefined) miss.add(`tx_${i}_post_tx_bloodwork`);
        if (t.post_tx_bloodwork === true && !t.post_tx_result) miss.add(`tx_${i}_post_tx_result`);
      });
    }
    if (!f.staff) miss.add("staff");
    if (!f.outcome) miss.add("outcome");
    return miss;
  };
  const surveyMiss = surveyTried ? missingSurveyFields(surveyForm) : new Set();
  const errBox = (k) => (surveyMiss.has(k) ? { outline: "2px solid #B4351B", outlineOffset: 2, borderRadius: 10 } : {});
  const errInput = (k) => (surveyMiss.has(k) ? { border: "1.5px solid #B4351B", background: "#FDF3F1" } : {});

  const saveSurvey = async () => {
    const f = surveyForm;
    // Every field is mandatory except the client's email and phone.
    const miss = missingSurveyFields(f);
    if (miss.size) {
      setSurveyTried(true);
      const first = ["prev_ab", "prev_rna", "prev_rna_result", "prev_rna_date", "prev_tx", "treatments", "risk_addiction_tx", "risk_pipes", "risk_needles", "liver_inflammation", "staff", "outcome"].find((k) => miss.has(k));
      const msgs = {
        prev_ab: "Previous Ab+: pick Yes or No", prev_rna: "Previous RNA test: pick Yes or No",
        prev_rna_result: "Pick the RNA result (positive or negative)", prev_rna_date: "Enter the RNA test date",
        prev_tx: "Previously treated: pick Yes or No", treatments: "Add at least one treatment, or set Previously treated to No",
        risk_addiction_tx: "Addiction treatment: pick Yes or No", risk_pipes: "Sharing pipes / stems: pick Yes or No",
        risk_needles: "Sharing needles / equipment: pick Yes or No", liver_inflammation: "Liver inflammation: pick Yes or No",
        staff: "Pick the staff member who completed the survey", outcome: "Pick the treatment qualification outcome",
      };
      if (first) return toast.error(msgs[first]);
      return toast.error("Complete the highlighted treatment fields");
    }
    setSaving(true);
    try {
      if (editingSurveyId) {
        await sapi.patch(`/surveys/${editingSurveyId}`, f);
        toast.success("Survey updated");
      } else {
        await sapi.post(`/clients/${profile.id}/surveys`, f);
        toast.success("Survey saved — $20 payment is pending approval");
      }
      setSurveyOpen(false);
      setEditingSurveyId(null);
      setSurveyTried(false);
      await openProfile(profile.id);
      loadDash();
      loadPayments(payStatusFilter);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === "survey_too_soon") {
        toast.error(`Already surveyed ${fmtD(det.last_survey_date)} — eligible again ${fmtD(det.eligible_date)}`);
      } else {
        toast.error(typeof det === "string" ? det : "Save failed");
      }
    } finally { setSaving(false); }
  };

  const payAction = async (sid, action) => {
    try {
      await sapi.post(`/surveys/${sid}/${action}`);
      toast.success(action === "approve" ? "Payment approved" : "Marked as paid");
      loadPayments(payStatusFilter);
      loadDash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const deleteClient = async () => {
    try {
      const { data } = await sapi.delete(`/clients/${confirmDelete.id}`);
      toast.success(`${data.name} removed`);
      setConfirmDelete(null);
      if (search.trim()) loadClients(search.trim());
      loadDash();
      loadPayments(payStatusFilter);
    } catch { toast.error("Delete failed"); }
  };

  const sf = (k) => (e) => setSurveyForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const cf = (k) => (e) => setClientForm((f) => ({ ...f, [k]: e.target.value }));
  const setTx = (i, k, v) => setSurveyForm((f) => ({ ...f, treatments: f.treatments.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));

  const duplicateMatches = useMemo(
    () => (search ? clients.filter((c) => !c.can_survey) : []),
    [search, clients],
  );

  // Distinct cities for the pharmacies popup filter.
  const pharmacyCities = useMemo(
    () => [...new Set(pharmacies.map((p) => p.city).filter(Boolean))].sort(),
    [pharmacies],
  );
  const filteredPharmacies = useMemo(
    () => pharmacies.filter((p) =>
      (!pharmacyCityFilter || p.city === pharmacyCityFilter) &&
      (!dashType || (p.site_type || "Pharmacy") === dashType)),
    [pharmacies, pharmacyCityFilter, dashType],
  );
  // Same city list reused inside the Add-patient dialog's location picker.
  const addClientPharmacies = useMemo(
    () => pharmacies.filter((p) =>
      (!newClientCityFilter || p.city === newClientCityFilter) &&
      (!newClientTypeFilter || (p.site_type || "Pharmacy") === newClientTypeFilter)),
    [pharmacies, newClientCityFilter, newClientTypeFilter],
  );

  const chip = (txt, col, bg) => (
    <span style={{ display: "inline-flex", alignItems: "center", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, padding: "4px 9px", borderRadius: 999, color: col, background: bg, lineHeight: 1.4, whiteSpace: "nowrap" }}>{txt}</span>
  );

  const clientCard = (c) => (
    <div key={c.id} data-testid={`survey-client-${c.id}`} style={{ ...card, padding: "12px 14px", marginBottom: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 8 }}
      onClick={() => openProfile(c.id)}>
      <div style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: NAVY, textAlign: "left" }}>{c.first_name} {c.last_name}</div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 2, textAlign: "left" }}>
          DOB {c.dob}{c.health_card ? ` · HC ${c.health_card}${c.version_code ? ` ${c.version_code}` : ""}` : ""}
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4, marginLeft: -9 }}>
          {c.last_outcome ? chip(outcomeMeta(c.last_outcome).label.split(" — ")[0], outcomeMeta(c.last_outcome).col, `${outcomeMeta(c.last_outcome).col}18`) : chip("No survey yet", MUTED, "#EDF1F4")}
          {chip(c.survey_count === 1 ? "1 survey" : `${c.survey_count} surveys`, NAVY, "#EDF1F4")}
        </div>
      </div>
      <button type="button" data-testid={`survey-client-delete-${c.id}`} style={{ ...btnGhost, color: RED, alignSelf: "flex-start", flexShrink: 0 }}
        onClick={(e) => { e.stopPropagation(); setConfirmDelete(c); }}>
        <Trash2 size={13} />
      </button>
    </div>
  );

  const openEditSite = async (p) => {
    try {
      const { data } = await ppApi.get("/pharmacies");
      const full = (data.items || []).find((x) => x.id === p.id);
      if (!full) return toast.error("Could not load that site");
      setEditingSite(full);
      setShowAddPharmacy(true);
    } catch {
      toast.error("Could not load that site");
    }
  };
  const deleteSite = async (p) => {
    if (!p.removable) return toast.error(`${p.name} can't be deleted — clients or surveys are linked to it`);
    try {
      await sapi.delete(`/pharmacies/${p.id}`);
      toast.success(`${p.name} removed`);
      loadPharmacies();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="survey-panel" style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* ================= DASHBOARD ================= */}
      {view === "dashboard" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", flexWrap: "nowrap", gap: 8, alignItems: "center", justifyContent: "flex-start" }}>
            <button type="button" data-testid="survey-pharmacy-manage-toggle" style={{ ...btn(GREEN), display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 14px", fontSize: 12, whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => setShowManagePharmacies(true)}>
              <Plus size={12} /> Add site
            </button>
            <button data-testid="survey-new-intake" style={{ ...btn(GREEN), display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 14px", fontSize: 12, whiteSpace: "nowrap", flexShrink: 0 }} onClick={startIntake}><Plus size={12} /> Add patient</button>
            <select data-testid="survey-dash-staff" style={{ ...input, width: "auto", minWidth: 0, flex: 1, height: 34, fontSize: 12 }} value={dashStaff} onChange={(e) => setDashStaff(e.target.value)}>
              <option value="">All</option>
              {visibleStaff.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button type="button" data-testid="survey-staff-manage" title="Manage survey staff" onClick={() => { setShowStaffMgr(true); setRenameName(""); setNewStaff(""); }}
              style={{ ...btnGhost, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, padding: 0, flexShrink: 0 }}>
              <Users size={14} />
            </button>
          </div>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 12, color: MUTED }} />
            <input data-testid="survey-search" style={{ ...input, paddingLeft: 30 }} placeholder="Search patient by name, DOB or health card #…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {search.trim() && (
            <div>
              {duplicateMatches.length > 0 && (
                <div data-testid="survey-duplicate-warning" style={{ background: "#FBF3DC", border: "1px solid #EAD9A0", borderRadius: 10, padding: "10px 12px", fontSize: 12, color: "#7A5A00", marginBottom: 8, lineHeight: 1.5 }}>
                  <b>Possible match:</b> {duplicateMatches.map((c) => (
                    <div key={c.id}><b>{c.first_name} {c.last_name}</b> was surveyed {fmtD(c.last_survey_date)} — eligible again <b>{fmtD(c.eligible_date)}</b>. Open the profile to view history.</div>
                  ))}
                </div>
              )}
              {clients.length === 0
                ? <div style={{ fontSize: 12, color: MUTED }} data-testid="survey-search-empty">No patients found — use <b>Add patient</b> to register this caller.</div>
                : clients.map(clientCard)}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
            <select data-testid="survey-dash-city" style={{ ...input, width: "100%" }} value={pharmacyCityFilter} onChange={(e) => { setPharmacyCityFilter(e.target.value); setDashFilter(""); }}>
              <option value="">City</option>
              {pharmacyCities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="survey-dash-type" style={{ ...input, width: "100%" }} value={dashType} onChange={(e) => { setDashType(e.target.value); setDashFilter(""); }}>
              <option value="">Type</option>
              {siteTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select data-testid="survey-dash-pharmacy" style={{ ...input, width: "100%" }} value={dashFilter} onChange={(e) => {
              // Picking a location directly backfills its city + type.
              const p = pharmacies.find((x) => x.id === e.target.value) || null;
              setDashFilter(e.target.value);
              if (p) {
                if (p.city) setPharmacyCityFilter(p.city);
                setDashType(p.site_type || "Pharmacy");
              }
            }}>
              <option value="">Location</option>
              {filteredPharmacies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select data-testid="survey-dash-month" style={{ ...input, width: "100%" }} value={dashMonth} onChange={(e) => setDashMonth(e.target.value)}>
              <option value="">Month</option>
              {[...Array(12)].map((_, i) => {
                const d = new Date(new Date().getFullYear(), new Date().getMonth() - i, 1);
                const ym = d.toISOString().slice(0, 7);
                return <option key={ym} value={ym}>{d.toLocaleDateString("en-CA", { month: "short", year: "numeric" })}</option>;
              })}
            </select>
          </div>

          {dash && (
            <div data-testid="survey-tiles" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                ...outcomes.map((o) => ({ k: o.key, label: o.label, col: o.col })),
                { k: "payments_paid", label: "Payments", col: NAVY },
              ].map((t) => (
                <button key={t.k} type="button" data-testid={`survey-tile-${t.k}`}
                  onClick={() => {
                    if (t.k === "payments_paid") {
                      setTileFilter("payments");
                      setPayStatusFilter("paid");
                    } else {
                      setTileFilter(t.k);
                      setPharmacy(null);
                      loadClients("", dashFilter, t.k, dashMonth, dashStaff, pharmacyCityFilter, dashType);
                      setView("dashboard");
                    }
                  }}
                  style={{ background: "#fff", border: `1px solid ${BORDER}`, borderLeft: `4px solid ${t.col}`, borderRadius: 12, padding: "12px 14px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                  <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1, color: t.col }}>{t.k === "payments_paid" ? dash.payments_paid : (dash.counts || {})[t.k] || 0}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: MUTED, marginTop: 5 }}>{t.label}</div>
                </button>
              ))}
            </div>
          )}

          {tileFilter && tileFilter !== "payments" && (
            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, height: 30 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: outcomeMeta(tileFilter).col, lineHeight: "30px" }}>{outcomeMeta(tileFilter).label.split(" — ")[0]} ({clients.length})</div>
                <button type="button" style={{ ...btnGhost, fontSize: 11, padding: "0 10px", height: 30 }} onClick={() => setTileFilter("")}>Clear</button>
              </div>
              {clients.length === 0 && <div style={{ fontSize: 12, color: MUTED }}>No clients in this group.</div>}
              {clients.map(clientCard)}
            </div>
          )}

          {(tileFilter === "payments" || tileFilter === "") && (
            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, height: 30 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: NAVY, lineHeight: "30px" }}>Survey payments ({payStatusFilter ? payments.length : 0})</div>
                <select data-testid="survey-payments-filter" style={{ ...btnGhost, height: 30, fontSize: 11, padding: "0 10px", flexShrink: 0, appearance: "auto", cursor: "pointer", fontWeight: 600 }} value={payStatusFilter} onChange={(e) => setPayStatusFilter(e.target.value)}>
                  <option value="">Select…</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              {payments.length === 0 && <div style={{ fontSize: 12, color: MUTED }}>{payStatusFilter ? `No ${payStatusFilter} payments.` : "Choose Pending or Paid to view payments."}</div>}
              {payments.map((p) => (
                <div key={p.id} data-testid={`survey-payment-${p.id}`}
                  style={{ ...card, padding: "12px 14px", marginBottom: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 8 }}
                  onClick={() => openProfile(p.client_id)}>
                  <div style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: NAVY, textAlign: "left" }}>{p.client_name}</div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 2, textAlign: "left" }}>
                      DOB {p.dob || "—"}{p.health_card ? ` · HC ${p.health_card}${p.version_code ? ` ${p.version_code}` : ""}` : ""}
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4, marginLeft: -9 }}>
                      {p.outcome ? chip(outcomeMeta(p.outcome).label.split(" — ")[0], outcomeMeta(p.outcome).col, `${outcomeMeta(p.outcome).col}18`) : null}
                      {p.staff ? chip(p.staff, NAVY, "#EDF1F4") : null}
                      {p.pay_status === "paid" ? chip(`Paid ${fmtD(p.pay_paid_at)}`, GREEN, "#E2F2E8") : chip("Pending", AMBER, "#FBF3DC")}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0, alignSelf: "flex-start" }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: NAVY }}>$20</div>
                    {(p.pay_status === "pending" || p.pay_status === "approved") && (
                      <button data-testid={`survey-payment-paid-${p.id}`} style={btn(GREEN)}
                        onClick={(e) => { e.stopPropagation(); payAction(p.id, "paid"); }}>Mark paid</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================= ADD / EDIT PATIENT (dialog — pharmacy picked
          inside the form; one step from the dashboard or profile) =========== */}
      <Dialog open={view === "newClient"} onOpenChange={(o) => !o && (setView("dashboard"), editingClient && openProfile(editingClient))}>
        <DialogContent data-testid="survey-new-client-dialog" className="max-w-md max-h-[92vh] overflow-y-auto overflow-x-hidden [&>button]:hidden" style={{ width: "calc(100vw - 24px)", maxWidth: 448 }}>
          <DialogHeader className="text-left sm:text-left" style={{ padding: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <DialogTitle style={{ fontSize: 17, fontWeight: 800, color: NAVY, textAlign: "left" }}>{editingClient ? "Edit patient" : "Add patient"}</DialogTitle>
                <DialogDescription asChild>
                  <span style={{ color: MUTED, fontSize: 12, marginTop: 2, display: "block", textAlign: "left" }}>{editingClient ? "Update the profile details." : "Save the profile, then the survey form opens."}</span>
                </DialogDescription>
              </div>
              <button type="button" aria-label="Close" data-testid="survey-new-client-close" onClick={() => { setView("dashboard"); if (editingClient) openProfile(editingClient); }}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", color: MUTED, cursor: "pointer", flexShrink: 0 }}>
                <X size={14} />
              </button>
            </div>
          </DialogHeader>

          <div>
            {/* City + Type side by side; Location gets its own full row
                because location names are usually longer. */}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={label}>City</label>
                <select data-testid="survey-client-city" style={{ ...input, width: "100%" }} value={newClientCityFilter} onChange={(e) => { setNewClientCityFilter(e.target.value); setPharmacy(null); }}>
                  <option value="">Select</option>
                  {pharmacyCities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={label}>Type</label>
                <select data-testid="survey-client-type" style={{ ...input, width: "100%" }} value={newClientTypeFilter} onChange={(e) => { setNewClientTypeFilter(e.target.value); setPharmacy(null); }}>
                  <option value="">Select</option>
                  {siteTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginTop: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={label}>Location *</label>
                <select data-testid="survey-client-pharmacy" style={{ ...input, width: "100%" }} value={pharmacy?.id || ""} onChange={(e) => {
                  // Picking a location directly backfills its city + type.
                  const p = pharmacies.find((x) => x.id === e.target.value) || null;
                  setPharmacy(p);
                  if (p) {
                    if (p.city) setNewClientCityFilter(p.city);
                    setNewClientTypeFilter(p.site_type || "Pharmacy");
                  }
                }}>
                  <option value="">Select</option>
                  {addClientPharmacies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={label}>First name *</label><input data-testid="survey-client-first" style={input} value={clientForm.first_name} onChange={cf("first_name")} /></div>
              <div><label style={label}>Last name *</label><input data-testid="survey-client-last" style={input} value={clientForm.last_name} onChange={cf("last_name")} /></div>
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={label}>Date of birth *</label><input data-testid="survey-client-dob" type="date" style={input} value={clientForm.dob} onChange={cf("dob")} /></div>
              <div><label style={label}>Age</label><input data-testid="survey-client-age" style={{ ...input, cursor: "default" }} value={ageFromDob(clientForm.dob)} readOnly tabIndex={-1} /></div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={label}>Health card #</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 72px", gap: 8 }}>
                <input data-testid="survey-client-hc" style={input} value={clientForm.health_card}
                  onChange={(e) => {
                    const { digits, letters } = splitHCN(e.target.value);
                    setClientForm((f) => ({ ...f, health_card: digits, version_code: letters || f.version_code }));
                  }}
                  inputMode="numeric" maxLength={10} />
                <input data-testid="survey-client-vc" style={input} value={clientForm.version_code}
                  onChange={(e) => setClientForm((f) => ({ ...f, version_code: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) }))}
                  maxLength={2} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button data-testid="survey-client-save" style={{ ...btn(GREEN), flex: 1 }} disabled={saving} onClick={saveClient}>{saving ? "Saving…" : (editingClient ? "Save profile" : "Save & start survey")}</button>
              <button style={{ ...btnGhost, flex: 1 }} onClick={() => { setView("dashboard"); if (editingClient) openProfile(editingClient); }}>Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ================= CLIENT PROFILE (dialog — same chrome as the
          consultation record dialog) ================= */}
      <Dialog open={!!profile && !surveyOpen} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent data-testid="survey-profile-dialog" className="max-w-md max-h-[92vh] overflow-y-auto overflow-x-hidden [&>button]:hidden" style={{ width: "calc(100vw - 24px)", maxWidth: 448 }}>
          {profile && (
            <>
              <DialogHeader className="text-left sm:text-left" style={{ padding: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, minWidth: 0 }}>
                  <div style={{ minWidth: 0, textAlign: "left" }}>
                    <DialogTitle data-testid="survey-profile-name" style={{ fontSize: 17, fontWeight: 800, color: NAVY, textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                      {profile.first_name} {profile.last_name}
                    </DialogTitle>
                    <DialogDescription asChild>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2, textAlign: "left" }}>
                        <span style={{ color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                          DOB {profile.dob}
                        </span>
                        {profile.health_card ? (
                          <span style={{ color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                            HC {profile.health_card}{profile.version_code ? ` ${profile.version_code}` : ""}
                          </span>
                        ) : null}
                      </div>
                    </DialogDescription>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    {profile.can_survey && (
                      <button data-testid="survey-add-survey" style={{ ...btn(NAVY), height: 26, padding: "0 9px", fontSize: 10.5, flexShrink: 0, whiteSpace: "nowrap" }} onClick={startSurvey}>+ Add</button>
                    )}
                    <button type="button" data-testid="survey-profile-edit" title="Edit patient details" onClick={openEditClient}
                      style={{ display: "inline-flex", alignItems: "center", gap: 3, height: 26, padding: "0 7px", background: "#fff", color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 6, cursor: "pointer", fontSize: 10.5, fontWeight: 600, fontFamily: "inherit" }}>
                      Edit
                    </button>
                    <button type="button" data-testid="survey-profile-copy" title="Copy name / DOB / HCN for a new consultation" onClick={() => copyClientInfo(profile)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 3, height: 26, padding: "0 7px", background: "rgba(15,59,46,0.06)", color: "#0F3B2E", border: "1px solid rgba(15,59,46,0.25)", borderRadius: 6, cursor: "pointer", fontSize: 10.5, fontWeight: 600, fontFamily: "inherit" }}>
                      <Copy size={10} style={{ verticalAlign: -1 }} /> Copy
                    </button>
                    <button type="button" aria-label="Close" data-testid="survey-profile-close" onClick={() => setProfile(null)}
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 6, border: `1px solid ${BORDER}`, background: "#fff", color: MUTED, cursor: "pointer", flexShrink: 0 }}>
                      <X size={12} />
                    </button>
                  </div>
                </div>
              </DialogHeader>

              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#4E8878", textTransform: "uppercase", letterSpacing: 1.2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Survey history ({profile.survey_count})</div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {(profile.surveys || []).length > 0 && (
                      <button data-testid={`survey-history-edit-${profile.surveys[profile.surveys.length - 1].id}`} style={{ ...btnGhost, fontSize: 11, padding: "4px 10px", height: 26 }} onClick={() => openEditSurvey(profile.surveys[profile.surveys.length - 1])}>View / Edit</button>
                    )}
                    <button data-testid="survey-add-survey" style={{ ...btn(NAVY), padding: "5px 10px", fontSize: 11, flexShrink: 0, whiteSpace: "nowrap" }} onClick={startSurvey}>+ Add</button>
                  </div>
                </div>
                {(profile.surveys || []).map((s) => (
                  <div key={s.id} data-testid={`survey-history-${s.id}`} onClick={() => openEditSurvey(s)}
                    style={{ borderLeft: "3px solid #4E8878", background: "#F7F6F2", borderRadius: "0 9px 9px 0", padding: "10px 12px", marginBottom: 8, maxWidth: "100%", boxSizing: "border-box", cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: NAVY, wordBreak: "break-word", minWidth: 0 }}>Survey #{s.seq} • {fmtD(s.date)}</div>
                    </div>
                    <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2, wordBreak: "break-word" }}>
                      {outcomeMeta(s.outcome).label}{s.staff ? ` · ${s.staff}` : ""}
                    </div>
                    {s.email && (
                      <div style={{ fontSize: 11.5, color: MUTED, wordBreak: "break-word" }}>{s.email}</div>
                    )}
                    <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {(s.pay_status === "pending" || s.pay_status === "approved") && <button data-testid={`survey-history-paid-${s.id}`} style={btn(GREEN)} onClick={(e) => { e.stopPropagation(); payAction(s.id, "paid").then(() => openProfile(profile.id)); }}>Mark paid</button>}
                      {s.pay_status !== "paid" && (
                        <button data-testid={`survey-history-delete-${s.id}`} style={{ ...btnGhost, color: RED }} onClick={async (e) => { e.stopPropagation(); await sapi.delete(`/surveys/${s.id}`); toast.success("Survey removed"); openProfile(profile.id); loadDash(); }}>Delete</button>
                      )}
                    </div>
                  </div>
                ))}
                {(profile.surveys || []).length === 0 && <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>No surveys yet.</div>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ================= SURVEY FORM (dialog) ================= */}
      <Dialog open={surveyOpen && !!profile} onOpenChange={(o) => !o && setSurveyOpen(false)}>
        <DialogContent data-testid="survey-form" className="max-w-md max-h-[92vh] overflow-y-auto overflow-x-hidden [&>button]:hidden" style={{ width: "calc(100vw - 24px)", maxWidth: 448 }}>
          {profile && (
            <>
              <DialogHeader className="text-left sm:text-left" style={{ padding: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, minWidth: 0 }}>
                  <div style={{ minWidth: 0, textAlign: "left" }}>
                    <DialogTitle style={{ fontSize: 17, fontWeight: 800, color: NAVY, textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                      {editingSurveyId ? "Edit survey" : "New survey"} — {profile.first_name} {profile.last_name}
                    </DialogTitle>
                    <DialogDescription asChild>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2, textAlign: "left" }}>
                        <span style={{ color: TEXT, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>DOB {profile.dob}</span>
                        {profile.health_card ? (
                          <span style={{ color: TEXT, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>HC {profile.health_card}{profile.version_code ? ` ${profile.version_code}` : ""}</span>
                        ) : null}
                      </div>
                    </DialogDescription>
                  </div>
                  <button type="button" aria-label="Close" data-testid="survey-form-close" onClick={() => setSurveyOpen(false)}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", color: MUTED, cursor: "pointer", flexShrink: 0 }}>
                    <X size={14} />
                  </button>
                </div>
              </DialogHeader>

              <div style={sech}>HCV history</div>
              <div style={{ marginBottom: 10 }}><label style={labelFlush}>Previous Ab+ (antibody positive) *</label><div style={errBox("prev_ab")}><YesNo testId="survey-prev-ab" value={surveyForm.prev_ab} onChange={sf("prev_ab")} /></div></div>
              <div style={{ marginBottom: 10 }}><label style={labelFlush}>Previous RNA test *</label><div style={errBox("prev_rna")}><YesNo testId="survey-prev-rna" value={surveyForm.prev_rna} onChange={sf("prev_rna")} /></div></div>
              {surveyForm.prev_rna === true && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={labelFlush}>RNA result *</label>
                    <div style={errBox("prev_rna_result")}><YesNo testId="survey-rna-result" value={surveyForm.prev_rna_result === "positive" ? true : surveyForm.prev_rna_result === "negative" ? false : null}
                      onChange={(v) => setSurveyForm((f) => ({ ...f, prev_rna_result: v === true ? "positive" : "negative" }))} yesLabel="Positive" noLabel="Negative" /></div>
                  </div>
                  <div>
                    <label style={label}>RNA test date *</label>
                    <input type="date" data-testid="survey-rna-date" style={{ ...input, ...errInput("prev_rna_date") }} value={surveyForm.prev_rna_date} onChange={sf("prev_rna_date")} />
                  </div>
                </div>
              )}
              <div style={{ marginBottom: 10 }}><label style={labelFlush}>Previously treated (Tx) *</label><div style={errBox("prev_tx")}><YesNo testId="survey-prev-tx" value={surveyForm.prev_tx} onChange={sf("prev_tx")} /></div></div>

              {surveyForm.prev_tx === true && (
                <div style={{ marginBottom: 6 }}>
                  {surveyForm.treatments.map((t, i) => (
                    <div key={i} data-testid={`survey-tx-${i}`} style={{ border: `1.5px dashed ${BORDER}`, borderRadius: 12, padding: 12, marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: NAVY }}>Treatment {i + 1}</span>
                        <button type="button" style={{ ...btnGhost, color: RED, padding: "3px 8px" }} onClick={() => setSurveyForm((f) => ({ ...f, treatments: f.treatments.filter((_, j) => j !== i) }))}><X size={12} /></button>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <label style={{ ...label, marginBottom: 0 }}>Medication *</label>
                        <button type="button" data-testid={`survey-tx-med-manage-${i}`} title="Manage medications" onClick={() => setManageMedsOpen(true)}
                          style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 4, height: 26, padding: "0 9px", fontSize: 11, flexShrink: 0 }}>
                          <Settings2 size={11} style={{ verticalAlign: -1 }} /> Manage
                        </button>
                      </div>
                      <select data-testid={`survey-tx-med-${i}`} style={{ ...input, marginBottom: 8, ...errInput(`tx_${i}_medication`) }} value={t.medication} onChange={(e) => setTx(i, "medication", e.target.value)}>
                        <option value="">— Select medication —</option>
                        {medications.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                      </select>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
                        <div><label style={label}>Initial dispense *</label><input type="date" data-testid={`survey-tx-initial-${i}`} style={{ ...input, ...errInput(`tx_${i}_initial_dispense`) }} value={t.initial_dispense} onChange={(e) => setTx(i, "initial_dispense", e.target.value)} /></div>
                        <div><label style={label}>Total dispensed *</label><input type="number" min="0" inputMode="numeric" data-testid={`survey-tx-total-${i}`} style={{ ...input, ...errInput(`tx_${i}_total_dispensed`) }} value={t.total_dispensed} onChange={(e) => setTx(i, "total_dispensed", e.target.value)} /></div>
                      </div>
                      <div style={{ marginBottom: 8 }}><label style={labelFlush}>Post-treatment bloodwork? *</label><div style={errBox(`tx_${i}_post_tx_bloodwork`)}><YesNo testId={`survey-tx-bw-${i}`} value={t.post_tx_bloodwork} onChange={(v) => setTx(i, "post_tx_bloodwork", v)} /></div></div>
                      {t.post_tx_bloodwork === true && (
                        <div><label style={labelFlush}>Post-treatment result *</label><div style={errBox(`tx_${i}_post_tx_result`)}><YesNo testId={`survey-tx-result-${i}`} value={t.post_tx_result === "negative" ? true : t.post_tx_result === "positive" ? false : null} onChange={(v) => setTx(i, "post_tx_result", v ? "negative" : "positive")} yesLabel="Negative" noLabel="Positive" /></div></div>
                      )}
                    </div>
                  ))}
                  <button type="button" data-testid="survey-tx-add" style={{ ...btnGhost, width: "100%", ...errBox("treatments") }} onClick={() => setSurveyForm((f) => ({ ...f, treatments: [...f.treatments, { ...EMPTY_TX }] }))}>+ Add another treatment</button>
                </div>
              )}

              <div style={sech}>Risk factors</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                {[["risk_addiction_tx", "Addiction treatment"], ["risk_pipes", "Sharing pipes / stems"], ["risk_needles", "Sharing needles / equipment"]].map(([k, lbl]) => (
                  <div key={k}>
                    <label style={labelFlush}>{lbl} *</label>
                    <div style={errBox(k)}><YesNo testId={`survey-${k.replace(/_/g, "-")}`} value={surveyForm[k]} onChange={sf(k)} /></div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 10 }}><label style={labelFlush}>Liver inflammation *</label><div style={errBox("liver_inflammation")}><YesNo testId="survey-liver" value={surveyForm.liver_inflammation} onChange={sf("liver_inflammation")} /></div></div>

              <div style={sech}>Outcome &amp; payment</div>
              <div style={{ marginBottom: 10 }}>
                <label style={label}>Completed by *</label>
                <select data-testid="survey-staff" style={{ ...input, ...errInput("staff") }} value={surveyForm.staff} onChange={sf("staff")}>
                  <option value="">— Select staff —</option>
                  {staffOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <label style={{ ...label, marginBottom: 0 }}>Treatment qualification *</label>
                  <button type="button" data-testid="survey-outcome-manage" title="Manage outcomes" onClick={() => { setShowOutcomeMgr(true); setRenameOutcomeKey(""); setNewOutcome(""); }}
                    style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 4, height: 26, padding: "0 9px", fontSize: 11, flexShrink: 0 }}>
                    <Settings2 size={11} style={{ verticalAlign: -1 }} /> Manage
                  </button>
                </div>
                <select data-testid="survey-outcome" style={{ ...input, ...errInput("outcome") }} value={surveyForm.outcome} onChange={sf("outcome")}>
                  <option value="">— Select outcome —</option>
                  {outcomes.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={label}>Email</label>
                <input data-testid="survey-email" type="email" style={input} value={surveyForm.email} onChange={sf("email")} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={label}>Phone</label>
                <input data-testid="survey-phone" type="tel" inputMode="tel" style={input} value={surveyForm.phone}
                  onChange={(e) => setSurveyForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))} />
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button data-testid="survey-save" style={{ ...btn(GREEN), flex: 1 }} disabled={saving} onClick={saveSurvey}>{saving ? "Saving…" : (editingSurveyId ? "Update" : "Save")}</button>
                {editingSurveyId && (
                  <button data-testid="survey-delete" style={{ ...btnGhost, flex: 1, color: RED }} onClick={async () => {
                    try {
                      await sapi.delete(`/surveys/${editingSurveyId}`);
                      toast.success("Survey removed");
                      setSurveyOpen(false);
                      setEditingSurveyId(null);
                      openProfile(profile.id);
                      loadDash();
                      loadPayments(payStatusFilter);
                    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                  }}>Delete</button>
                )}
                <button style={{ ...btnGhost, flex: 1 }} onClick={() => { setSurveyOpen(false); setEditingSurveyId(null); }}>Cancel</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ================= MANAGE PHARMACIES (popup list with delete) ================= */}
      <Dialog open={showManagePharmacies} onOpenChange={setShowManagePharmacies}>
        <DialogContent data-testid="survey-pharmacies-dialog" className="max-w-md max-h-[92vh] overflow-y-auto overflow-x-hidden [&>button]:hidden" style={{ width: "calc(100vw - 24px)", maxWidth: 448 }}>
          <DialogHeader>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <DialogTitle style={{ fontSize: 17, fontWeight: 800, color: NAVY, minWidth: 0 }}>Sites</DialogTitle>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <button type="button" data-testid="survey-pharmacies-add" onClick={() => { setEditingSite(null); setShowManagePharmacies(false); setShowAddPharmacy(true); }}
                  style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 5, height: 30, padding: "0 10px", fontSize: 12, whiteSpace: "nowrap" }}>
                  <Plus size={12} /> Add site
                </button>
                <button type="button" aria-label="Close" data-testid="survey-pharmacies-close" onClick={() => setShowManagePharmacies(false)}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", color: MUTED, cursor: "pointer", flexShrink: 0 }}>
                  <X size={14} />
                </button>
              </div>
            </div>
          </DialogHeader>
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
              <select data-testid="survey-sites-city-filter" style={{ ...input, width: "100%" }} value={sitesCity} onChange={(e) => setSitesCity(e.target.value)}>
                <option value="">City</option>
                {pharmacyCities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="survey-sites-type-filter" style={{ ...input, width: "100%" }} value={sitesType} onChange={(e) => setSitesType(e.target.value)}>
                <option value="">Type</option>
                {siteTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {(() => {
              const rows = pharmacies.filter((p) =>
                (!sitesCity || p.city === sitesCity) &&
                (!sitesType || (p.site_type || "Pharmacy") === sitesType));
              if (rows.length === 0) return <div style={{ fontSize: 12, color: MUTED, padding: "8px 0" }}>No sites{sitesCity ? ` in ${sitesCity}` : " yet"}.</div>;
              return rows.map((p) => (
                <div key={p.id} data-testid={`survey-pharmacy-row-${p.id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 0", borderTop: `1px solid ${BORDER}`, fontSize: 13 }}>
                  <span style={{ minWidth: 0, fontWeight: 600, color: TEXT }}>{p.name}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <button type="button" title="Edit site" data-testid={`survey-pharmacy-edit-${p.id}`} onClick={() => openEditSite(p)}
                      style={{ ...btnGhost, width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <Pencil size={12} />
                    </button>
                    <button type="button" title={p.removable ? "Delete site" : "Linked to clients or surveys — can't delete"} data-testid={`survey-pharmacy-del-${p.id}`} onClick={() => deleteSite(p)}
                      style={{ ...btnGhost, width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: RED, opacity: p.removable ? 1 : 0.45 }}>
                      <Trash2 size={12} />
                    </button>
                  </span>
                </div>
              ));
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* ================= MANAGE MEDICATIONS (same dialog as consultation) = */}
      <Dialog open={manageMedsOpen} onOpenChange={setManageMedsOpen}>
        <DialogContent data-testid="survey-manage-meds-dialog" className="max-w-md max-h-[92vh] overflow-y-auto [&>button]:hidden" style={{ width: "calc(100vw - 24px)", maxWidth: 448 }}>
          <DialogHeader className="text-left sm:text-left" style={{ padding: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <DialogTitle style={{ fontSize: 17, fontWeight: 800, color: NAVY, textAlign: "left" }}>Manage medications</DialogTitle>
                <DialogDescription asChild>
                  <span style={{ color: MUTED, fontSize: 12, marginTop: 2, display: "block", textAlign: "left" }}>Shared with the consultation form</span>
                </DialogDescription>
              </div>
              <button type="button" aria-label="Close" data-testid="survey-manage-meds-close" onClick={() => setManageMedsOpen(false)}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 7, border: `1px solid ${BORDER}`, background: "#fff", color: MUTED, cursor: "pointer", flexShrink: 0 }}>
                <X size={13} />
              </button>
            </div>
          </DialogHeader>
          <ManageMedsBody medications={medications} onChanged={loadMeds} />
        </DialogContent>
      </Dialog>

      {/* Partner pharmacy dialog — the exact validated form from Pharmacy
          Partners, including the OCP register paste that fills address/phone/
          fax automatically */}
      <PharmacyFormDialog
        open={showAddPharmacy}
        onOpenChange={(o) => { setShowAddPharmacy(o); if (!o) setEditingSite(null); }}
        editing={editingSite}
        statuses={["Active", "Inactive"]}
        onAddStatus={async (v) => { try { await ppApi.post("/settings/statuses", { value: v }); } catch { /* status exists */ } }}
        onSaved={() => { setShowAddPharmacy(false); setEditingSite(null); loadPharmacies(); }}
      />

      {/* ================= DELETE CONFIRM ================= */}
      {/* ================= MANAGE SURVEY STAFF ================= */}
      {showStaffMgr && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,32,56,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}
          onClick={() => setShowStaffMgr(false)}>
          <div style={{ ...card, maxWidth: 380, width: "100%", maxHeight: "80vh", overflowY: "auto" }} data-testid="survey-staff-manager" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: NAVY }}>Survey staff</div>
              <button type="button" aria-label="Close" data-testid="survey-staff-manager-close" onClick={() => setShowStaffMgr(false)}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", color: MUTED, cursor: "pointer", flexShrink: 0 }}>
                <X size={14} />
              </button>
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input data-testid="survey-staff-add-input" style={{ ...input, flex: 1, height: 34, fontSize: 12 }} placeholder="Add person" value={newStaff} onChange={(e) => setNewStaff(e.target.value)} />
              <button data-testid="survey-staff-add-btn" style={{ ...btn(GREEN), padding: "0 12px", height: 34, fontSize: 12, flexShrink: 0 }} onClick={addStaff} disabled={!newStaff.trim()}>Add</button>
            </div>
            {staffList.map((s) => (
              <div key={s.name} data-testid={`survey-staff-row-${s.name}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0", borderBottom: `1px solid ${BORDER}`, opacity: s.hidden ? 0.55 : 1 }}>
                {renameName === s.name ? (
                  <>
                    <input data-testid="survey-staff-rename-input" style={{ ...input, flex: 1, height: 32, fontSize: 12 }} value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
                    <button data-testid="survey-staff-rename-save" style={{ ...btn(GREEN), padding: "0 10px", height: 32, fontSize: 11, flexShrink: 0 }} onClick={saveStaffRename} disabled={!renameValue.trim()}>Save</button>
                    <button style={{ ...btnGhost, padding: "0 10px", height: 32, fontSize: 11, flexShrink: 0 }} onClick={() => setRenameName("")}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: TEXT, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.name}{s.hidden ? " (hidden)" : ""}
                    </span>
                    <button type="button" title={s.hidden ? "Show in dropdowns" : "Hide from dropdowns"} data-testid={`survey-staff-hide-${s.name}`} onClick={() => toggleHideStaff(s)}
                      style={{ ...btnGhost, width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {s.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                    <button type="button" title="Rename" data-testid={`survey-staff-rename-${s.name}`} onClick={() => { setRenameName(s.name); setRenameValue(s.name); }}
                      style={{ ...btnGhost, width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Pencil size={13} />
                    </button>
                    <button type="button" title={s.in_use ? "Has surveys — hide instead" : "Delete"} disabled={s.in_use} data-testid={`survey-staff-delete-${s.name}`} onClick={() => deleteStaff(s)}
                      style={{ ...btnGhost, width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: RED, opacity: s.in_use ? 0.35 : 1, cursor: s.in_use ? "not-allowed" : "pointer" }}>
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================= MANAGE OUTCOMES ================= */}
      {showOutcomeMgr && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,32,56,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}
          onClick={() => setShowOutcomeMgr(false)}>
          <div style={{ ...card, maxWidth: 380, width: "100%", maxHeight: "80vh", overflowY: "auto" }} data-testid="survey-outcome-manager" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: NAVY }}>Treatment qualification outcomes</div>
              <button type="button" aria-label="Close" data-testid="survey-outcome-manager-close" onClick={() => setShowOutcomeMgr(false)}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", color: MUTED, cursor: "pointer", flexShrink: 0 }}>
                <X size={14} />
              </button>
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input data-testid="survey-outcome-add-input" style={{ ...input, flex: 1, height: 34, fontSize: 12 }} placeholder="Add outcome" value={newOutcome} onChange={(e) => setNewOutcome(e.target.value)} />
              <button data-testid="survey-outcome-add-btn" style={{ ...btn(GREEN), padding: "0 12px", height: 34, fontSize: 12, flexShrink: 0 }} onClick={addOutcome} disabled={!newOutcome.trim()}>Add</button>
            </div>
            {outcomes.map((o) => (
              <div key={o.key} data-testid={`survey-outcome-row-${o.key}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                {renameOutcomeKey === o.key ? (
                  <>
                    <input data-testid="survey-outcome-rename-input" style={{ ...input, flex: 1, height: 32, fontSize: 12 }} value={renameOutcomeValue} onChange={(e) => setRenameOutcomeValue(e.target.value)} />
                    <button data-testid="survey-outcome-rename-save" style={{ ...btn(GREEN), padding: "0 10px", height: 32, fontSize: 11, flexShrink: 0 }} onClick={saveOutcomeRename} disabled={!renameOutcomeValue.trim()}>Save</button>
                    <button style={{ ...btnGhost, padding: "0 10px", height: 32, fontSize: 11, flexShrink: 0 }} onClick={() => setRenameOutcomeKey("")}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: o.col, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: TEXT, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                    <button type="button" title="Rename" data-testid={`survey-outcome-rename-${o.key}`} onClick={() => { setRenameOutcomeKey(o.key); setRenameOutcomeValue(o.label); }}
                      style={{ ...btnGhost, width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Pencil size={13} />
                    </button>
                    <button type="button" title={o.in_use ? "Surveys use this outcome — it can't be deleted" : "Delete"} disabled={!!o.in_use} data-testid={`survey-outcome-delete-${o.key}`} onClick={() => deleteOutcome(o)}
                      style={{ ...btnGhost, width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: RED, opacity: o.in_use ? 0.35 : 1, cursor: o.in_use ? "not-allowed" : "pointer" }}>
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,32,56,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}>
          <div style={{ ...card, maxWidth: 380, width: "100%" }} data-testid="survey-delete-confirm">
            <div style={{ fontWeight: 800, fontSize: 15, color: NAVY, marginBottom: 6 }}>Remove {confirmDelete.first_name} {confirmDelete.last_name}?</div>
            <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 14 }}>Their survey history is archived (never lost), and they won't appear in any list. This can't be undone from here.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button data-testid="survey-delete-yes" style={{ ...btn(RED), flex: 1 }} onClick={deleteClient}>Remove client</button>
              <button style={{ ...btnGhost, flex: 1 }} onClick={() => setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add / rename / delete medications against the shared consultation list —
// mirrors the consultation form's Manage medications dialog behavior.
function ManageMedsBody({ medications, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows(medications.map((m) => ({ ...m, _draft: m.name })));
  }, [medications]);

  const saveRow = async (r) => {
    const name = r._draft.trim();
    if (!name || name === r.name) return;
    setBusy(true);
    try {
      await consultApi.patch(`/medications/${r.id}`, { name });
      toast.success("Medication renamed");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    finally { setBusy(false); }
  };
  const deleteRow = async (r) => {
    if (!window.confirm(`Delete medication "${r.name}"?`)) return;
    setBusy(true);
    try {
      await consultApi.delete(`/medications/${r.id}`);
      toast.success("Medication deleted");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };
  const addRow = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await consultApi.post("/medications", { name });
      setNewName("");
      toast.success("Medication added");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        {rows.length === 0 && <div style={{ color: MUTED, fontSize: 13, padding: 12, textAlign: "center" }}>No medications yet — add one below.</div>}
        {rows.map((r, idx) => (
          <div key={r.id} data-testid={`survey-med-row-${r.id}`} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: 8 }}>
            <input
              value={r._draft}
              onChange={(e) => setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, _draft: e.target.value } : x)))}
              onBlur={() => saveRow(r)}
              data-testid={`survey-med-input-${r.id}`}
              style={{ flex: 1, minWidth: 0, border: 0, padding: 0, background: "transparent", fontSize: 14, color: TEXT, outline: "none", fontFamily: "inherit" }}
            />
            <button type="button" disabled={busy} onClick={() => deleteRow(r)} data-testid={`survey-med-del-${r.id}`} style={{ ...btnGhost, color: RED, padding: "3px 8px", flexShrink: 0 }}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
          placeholder="Add new medication…"
          data-testid="survey-med-new-input"
          style={{ ...input, flex: 1 }}
        />
        <button type="button" onClick={addRow} disabled={busy || !newName.trim()} data-testid="survey-med-add" style={{ ...btn(GREEN), flexShrink: 0 }}>Add</button>
      </div>
    </div>
  );
}
